"use client";

import { useState } from "react";
import { getCarrierLogo, carrierInitials } from "@/app/_module/lib/carrierLogos";

interface CarrierLogoProps {
  name: string | undefined | null;
  /** Display size in pixels. Bumped to 64px source resolution for crispness on retina. */
  size?: number;
  /** Visual style — `chip` for a softer rounded card surface, `bare` for unstyled (e.g. inside an existing card). */
  variant?: "chip" | "bare";
  className?: string;
}

/**
 * Carrier logo with graceful fallback. Tries the Google-favicon CDN URL from
 * the registry first; if the carrier isn't in the registry OR the image
 * fails to load (carrier domain changed, network hiccup), falls back to a
 * brand-colored circle with the carrier's initials.
 *
 * Used in quote-result cards, case ledger rows, and the lead-detail policy
 * panel — anywhere we display a carrier name beside a premium.
 */
export function CarrierLogo({
  name,
  size = 32,
  variant = "chip",
  className = "",
}: CarrierLogoProps) {
  const [loadFailed, setLoadFailed] = useState(false);
  const match = getCarrierLogo(name, 64);
  const initials = carrierInitials(name);

  const url = loadFailed ? null : match?.url;

  const baseClasses =
    variant === "chip"
      ? "rounded-md bg-white border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0"
      : "flex items-center justify-center flex-shrink-0";

  return (
    <span
      className={`${baseClasses} ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor:
          variant === "chip" && !url ? match?.brandColor || "#475569" : undefined,
      }}
      title={name || undefined}
    >
      {url ? (
        <img
          src={url}
          alt={name || "Carrier"}
          width={size}
          height={size}
          className="object-contain"
          style={{ maxWidth: size - 4, maxHeight: size - 4 }}
          // Google answers a domain it has no icon for with a 16px globe (no
          // error), and a real 16px icon blurs at this size: initials either way.
          onLoad={(e) => {
            if (e.currentTarget.naturalWidth <= 16) setLoadFailed(true);
          }}
          onError={() => setLoadFailed(true)}
        />
      ) : (
        <span
          className="font-bold text-white"
          style={{ fontSize: Math.max(10, Math.round(size * 0.4)) }}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

export default CarrierLogo;
